import { ApiResponse, PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  AddonResponse,
  AddonListQueryParams,
  UpdateAvailablePayload,
  IAddonData,
  AddonVatRegime,
  AddonVatRegimeListResponse,
} from "@/types/addon-management.types";

export const AddonManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAddons: builder.query<
      PaginatedApiResponse<AddonResponse[]>,
      AddonListQueryParams
    >({
      query: (params) => ({
        url: "addons",
        method: "GET",
        params: {
          ...params,
          harbor_id: params.harbor_id,
        },
      }),
      transformResponse: (response: PaginatedApiResponse<AddonResponse[]>) =>
        response,
      providesTags: ["Addons"],
      keepUnusedDataFor: 0,
    }),

    getAddonById: builder.query<AddonResponse, string>({
      query: (addon_id) => ({
        url: `addons/${addon_id}`,
        method: "GET",
      }),
      transformResponse: (response: AddonResponse) => response,
      providesTags: (result, error, addon_id) => [
        { type: "Addons", id: addon_id },
      ],
      keepUnusedDataFor: 0,
    }),

    getAddonVatRegimes: builder.query<AddonVatRegimeListResponse, void>({
      query: () => ({
        url: "addons/vat-regimes",
        method: "GET",
      }),
      providesTags: ["ProductVatConfigs"],
    }),

    getAddonVatRegimeById: builder.query<AddonVatRegime, string>({
      query: (id) => ({
        url: `addons/vat-regimes/${id}`,
        method: "GET",
      }),
      transformResponse: (response: { data: AddonVatRegime }) => response.data,
      providesTags: (result, error, id) => [{ type: "ProductVatConfigs", id }],
    }),

    createAddon: builder.mutation<ApiResponse<void>, Partial<IAddonData>>({
      query: (data) => ({
        url: "addons",
        method: "POST",
        data,
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Addons"],
    }),

    updateAddon: builder.mutation<
      ApiResponse<void>,
      { addon_id: string; data: Partial<IAddonData> }
    >({
      query: ({ addon_id, data }) => ({
        url: `addons/${addon_id}`,
        method: "PATCH",
        data,
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Addons"],
    }),

    deleteAddon: builder.mutation<ApiResponse<void>, string>({
      query: (addon_id) => ({
        url: `addons/${addon_id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Addons"],
    }),

    updateAvailableAddon: builder.mutation<
      ApiResponse<void>,
      UpdateAvailablePayload
    >({
      query: ({ addonId, is_available }) => ({
        url: "addons/status",
        method: "PATCH",
        data: { addonId, is_available },
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Addons"],
    }),

    translateAddon: builder.mutation<
      {
        status: boolean;
        message: string;
        warning_message?: string;
        data: {
          translations: Record<
            string,
            {
              name?: string;
              description?: string;
              key_box_pickup_instructions?: string;
              key_box_return_instructions?: string;
            }
          >;
        };
      },
      {
        addon_id?: string;
        name?: string;
        description?: string;
        source_language?: string;
        access_handover_mode?: string;
        key_box_pickup_instructions?: string;
        key_box_return_instructions?: string;
      }
    >({
      query: (data) => ({
        url: "addons/translate",
        method: "POST",
        data,
      }),
    }),

    downloadAddonQrCode: builder.mutation<Blob, string>({
      query: (id) => ({
        url: `addons/${id}/qr-code`,
        method: "GET",
        responseType: "blob",
      }),
      transformResponse: (response: unknown): Blob => response as Blob,
    }),
  }),
});

export const {
  useGetAddonsQuery,
  useGetAddonByIdQuery,
  useGetAddonVatRegimesQuery,
  useGetAddonVatRegimeByIdQuery,
  useCreateAddonMutation,
  useUpdateAddonMutation,
  useDeleteAddonMutation,
  useUpdateAvailableAddonMutation,
  useTranslateAddonMutation,
  useDownloadAddonQrCodeMutation,
} = AddonManagementService;
